// Serializes the System 404 lore model into a single Markdown document that
// visitors can download. The lore data IS the export: this walks the typed model
// and emits readable Markdown, so the downloadable file always matches the site.
//
// Formatting mirrors the canonical lore.md: no em dashes, hyphens where needed,
// bold lead-ins for bodies, sections grouped logically.

import { system404 } from './system'
import type { CelestialBody, Faction, SpeciesCategory } from './types'

const CATEGORY_ORDER: SpeciesCategory[] = ['aerial', 'land', 'ocean', 'unconfirmed']
const CATEGORY_LABEL: Record<SpeciesCategory, string> = {
  aerial: 'Aerial',
  land: 'Land',
  ocean: 'Ocean',
  unconfirmed: 'Unconfirmed',
}

const typeLabel: Record<string, string> = {
  star: 'Star',
  molten: 'Molten core world',
  rocky: 'Rocky planet',
  terrestrial: 'Terrestrial',
  'gas-giant': 'Gas giant',
  frozen: 'Frozen world',
  dwarf: 'Dwarf planet',
  debris: 'Debris field',
  'asteroid-belt': 'Asteroid belt',
}

/** Strip leading ** from a markdown string if present (used when we re-bold). */
const strip = (s: string): string => s.replace(/^\*\*/, '').replace(/\*\*$/, '')

/** Serialize the whole system into one Markdown string. */
export function loreToMarkdown(): string {
  const out: string[] = []

  // --- Title + overview ---
  out.push(`# ${system404.formalName}`)
  out.push(system404.overview)
  out.push('')
  out.push(`**Star name**: ${system404.star.name}`)
  out.push(`**System name by insiders**: ${system404.insiderName}`)
  out.push('')

  // --- System anomalies ---
  if (system404.anomalies.length) {
    out.push('**Anomalies of the system:**')
    for (const a of system404.anomalies) out.push(`- ${a}`)
    out.push('')
  }

  out.push('# The Worlds')
  out.push('')

  // --- The star ---
  if (system404.star.lore) {
    out.push(`## ${system404.star.name}`)
    out.push(`*${typeLabel[system404.star.type] ?? system404.star.type}*`)
    out.push('')
    out.push(system404.star.lore)
    out.push('')
  }

  // --- Each body, in orbit order ---
  for (const body of system404.bodies) {
    writeBody(out, body)
  }

  // --- The human layer (system-level prose) ---
  out.push('# The Human Layer')
  out.push('')
  if (system404.sosSignal) {
    out.push('## The SOS Signal')
    out.push(system404.sosSignal)
    out.push('')
  }
  if (system404.comms) {
    out.push('## Comms')
    out.push(system404.comms)
    out.push('')
  }
  if (system404.mechs) {
    out.push('## Extravehicular Units')
    out.push(system404.mechs)
    out.push('')
  }
  if (system404.economy) {
    out.push('## Economy')
    out.push(system404.economy)
    out.push('')
  }
  if (system404.livingNotes) {
    out.push('## Living in the Lie')
    out.push(system404.livingNotes)
    out.push('')
  }

  // --- Factions ---
  out.push('# Factions')
  out.push('')
  for (const f of system404.factions) {
    writeFaction(out, f)
  }

  // --- Faction relations ---
  if (system404.factionRelations) {
    out.push('## How the Factions Relate')
    out.push(system404.factionRelations)
    out.push('')
  }

  return out.join('\n')
}

/** Write one celestial body (and its moons/species/plants) to the output. */
function writeBody(out: string[], body: CelestialBody): void {
  out.push(`## ${body.name}`)
//   if (body.tagline) out.push(`*${strip(body.tagline)}*`)
//   out.push('')
  if (body.lore) {
    out.push(body.lore)
    out.push('')
  }

  // moons
  if (body.moons?.length) {
    out.push('### Moons')
    for (const m of body.moons) {
      out.push(`- **${m.name}**: ${strip(m.lore)}`)
    }
    out.push('')
  }

  // anomalies
  if (body.anomalies?.length) {
    out.push('### Anomalies')
    for (const a of body.anomalies) out.push(`- ${a}`)
    out.push('')
  }

  // layout notes
  if (body.layoutNotes) {
    out.push('### Planet Layout')
    out.push(body.layoutNotes)
    out.push('')
  }

  // fauna
  if (body.species?.length) {
    out.push('### Fauna')
    const hasCategories = body.species.some((s) => s.category)
    if (hasCategories) {
      for (const cat of CATEGORY_ORDER) {
        const items = body.species!.filter((s) => s.category === cat)
        if (!items.length) continue
        out.push(`#### ${CATEGORY_LABEL[cat]}`)
        for (const s of items) out.push(`- **${s.name}**: ${strip(s.lore)}`)
        out.push('')
      }
    } else {
      for (const s of body.species) out.push(`- **${s.name}**: ${strip(s.lore)}`)
      out.push('')
    }
  }

  // wildlife notes
  if (body.wildlifeNotes) {
    out.push('#### Wildlife in General')
    out.push(body.wildlifeNotes)
    out.push('')
  }

  // vegetation rules
  if (body.vegetationRules) {
    out.push('### Vegetation')
    out.push('#### General Vegetation Rules')
    out.push(body.vegetationRules)
    out.push('')
  }

  // key plants
  if (body.plants?.length) {
    out.push('#### Key Plants')
    for (const p of body.plants) out.push(`- **${p.name}**: ${strip(p.lore)}`)
    out.push('')
  }
}

/** Write one faction (and its subfactions / sections) to the output. */
function writeFaction(out: string[], f: Faction): void {
  out.push(`## ${f.name}`)
  out.push('')
  if (f.lore) {
    out.push(f.lore)
    out.push('')
  }
  // sections (e.g. the Helix Syndicate's leadership / seat / control / SOS)
  if (f.sections?.length) {
    for (const s of f.sections) {
      out.push(`### ${s.title}`)
      out.push(s.body)
      out.push('')
    }
  }
  // subfactions (e.g. the Tethered's Collaborators / Isolationists / Raiders)
  if (f.subfactions?.length) {
    for (const sub of f.subfactions) {
      out.push(`### ${sub.name}`)
      if (sub.lore) {
        out.push(sub.lore)
        out.push('')
      }
    }
  }
}
